// managers/pdarProbabilityField/PdarProbabilityFieldManager.ts
//
// Renders each active PDAR target's probability field as a hex-tiled grid of
// flat-colored hexagon polygons — this is a separate feature from
// HeatmapManager/the "heatmap" layer (layers/heatmap.ts), which stays
// untouched and keeps serving whatever else depends on it.
//
// HISTORY:
//   - MapLibre's native `heatmap` layer renders each point as a blurred blob
//     sized in fixed SCREEN PIXELS. At high zoom the real-world gap between
//     grid points exceeds that pixel radius and the field collapses into
//     separate spots — not tunable away, it's fundamental to point-blob
//     rendering.
//   - A canvas-raster approach (bilinearly interpolating the grid into a
//     smooth texture) fixed the "collapses into spots" problem and stayed
//     resolution-independent at any zoom, but visually smoothed away the
//     underlying grid/algorithm the data is actually computed on.
//
// CURRENT DESIGN: each target's probability field (a sparse list of hex
// cells — see ProbabilityGridPayload/ProbabilityGridCell) is rendered as
// actual hexagon polygons, one GeoJSON Feature per cell, colored by that
// cell's own weight (no blending across cells) via a single shared `fill`
// layer. This keeps the grid/algorithm visually legible (you can see
// individual hex cells, matching how the simulator actually computes the
// field) while still being resolution-independent at any zoom, since
// MapLibre renders vector polygon geometry natively at full precision
// regardless of zoom level — unlike the old point-blob heatmap, there's no
// fixed-pixel-radius to outgrow.
//
// EFFICIENCY: the simulator already does the heavy lifting here (ring-walk
// generation stops once weight falls below a cutoff, hard-capped per
// target — see multi.sim.ts's buildTargetGrid) so this manager only ever
// receives however many hex cells actually matter this tick, not a fixed
// worst-case area. All targets' hexes are combined into ONE shared GeoJSON
// source/layer (rather than one source per target) since polygon features
// don't have the same "one bounding box per source" constraint an image/
// canvas source has — a single setData() per batch is simpler and cheaper
// than per-target source/layer lifecycle management.
//
// Color ramp: dark blue/violet (low confidence) -> magenta/pink -> lime
// green -> orange, peaking near-white at the very center. Chosen to read
// clearly against a dark basemap without hard "containment ring" edges —
// combined with the weight-driven opacity ramp below, low-weight hexes are
// both dim AND desaturated toward near-invisible dark blue/violet, so the
// probability field fades out rather than terminating abruptly.
import type {
  Map as MapType,
  GeoJSONSource,
  ExpressionSpecification,
} from "maplibre-gl";
import type { MapEventBus } from "../../core/MapEventBus";
import type { ProbabilityGridPayload } from "../../types";
import { LAYERS } from "../../layers/constants";
import { convertToHexLattice } from "../../utils/hexLatticeConverter";

const SOURCE_ID = "pdar-probability-field-source";
const LAYER_ID = "pdar-probability-field-layer";

const METERS_PER_DEG_LAT = 111_320;
function metersPerDegLon(latDeg: number): number {
  return METERS_PER_DEG_LAT * Math.cos((latDeg * Math.PI) / 180);
}

// Classic heat spectrum: blue (low) -> cyan/green -> yellow -> orange ->
// red -> white (peak). Matches the ramp used before the raster->hex switch;
// kept identical since the color intent hasn't changed, only the shape
// each color is painted onto (hexagons instead of an interpolated texture).
// Expressed here as a MapLibre "interpolate" paint expression rather than a
// JS function, since coloring now happens per-feature via fill-color
// (data-driven from each Feature's `weight` property) instead of per-pixel
// in a canvas.
const FILL_COLOR_EXPRESSION: ExpressionSpecification = [
  "interpolate",
  ["linear"],
  ["coalesce", ["get", "weight"], 0],
  0,
  "rgba(8, 10, 40, 0)",
  0.12,
  "rgb(35, 25, 90)",
  0.28,
  "rgb(90, 40, 140)",
  0.45,
  "rgb(180, 60, 160)",
  0.6,
  "rgb(230, 90, 150)",
  0.72,
  "rgb(190, 220, 60)",
  0.85,
  "rgb(235, 235, 60)",
  0.94,
  "rgb(255, 240, 120)",
  1,
  "rgb(255, 253, 225)",
];

// Fill opacity fades in step with color, so low-weight hexes are both dim
// AND desaturated — no hard "containment ring" edge where the field just
// stops. Kept below 1.0 even at peak weight so hexagon edges (and whatever
// sits beneath the layer) stay faintly visible through the densest cell.
const FILL_OPACITY_EXPRESSION: ExpressionSpecification = [
  "interpolate",
  ["linear"],
  ["coalesce", ["get", "weight"], 0],
  0,
  0,
  0.1,
  0.05,
  0.3,
  0.18,
  0.5,
  0.35,
  0.7,
  0.55,
  0.9,
  0.75,
  1,
  0.88,
];

// Builds one hexagon's polygon ring (7 points — 6 vertices + closing point)
// centered on (centerLat, centerLon), in real-world meters converted to
// degrees at that latitude. Pointy-top orientation (flat sides left/right,
// points up/down) to match the axial hex layout the simulator generates
// centers on (see multi.sim.ts's hexToOffsetMeters).
function hexagonRing(
  centerLat: number,
  centerLon: number,
  hexRadiusM: number,
): [number, number][] {
  const latPerM = 1 / METERS_PER_DEG_LAT;
  const lonPerM = 1 / metersPerDegLon(centerLat);

  const ring: [number, number][] = [];
  for (let i = 0; i <= 6; i++) {
    // Pointy-top hexagon: vertices at 60deg increments, offset so they land
    // at the correct positions to share full edges with neighboring hexes
    // generated by the simulator's axial hexToOffsetMeters layout (verified
    // numerically — adjacent hex polygons share exactly 2 vertices/one edge
    // in all 6 axial directions). The -90deg offset here is just a
    // reference rotation; any 60deg-multiple offset produces the same
    // hexagon shape, since all that matters is the 6 vertices sit at the
    // right relative angles, not which one is listed first.
    const angleDeg = 60 * i - 90;
    const angleRad = (angleDeg * Math.PI) / 180;
    const eastM = hexRadiusM * Math.cos(angleRad);
    const northM = hexRadiusM * Math.sin(angleRad);
    ring.push([
      centerLon + eastM * lonPerM,
      centerLat + northM * latPerM,
    ]);
  }
  return ring;
}

export class PdarProbabilityFieldManager {
  private initialised = false;

  constructor(
    private map: MapType,
    private eventBus: MapEventBus,
  ) {}

  // PUBLIC API — called by PdarProbabilityFieldUpdater via the
  // probability-grid:batch WS topic.
  //
  // IMPORTANT: cells arriving on this topic are NOT assumed to already sit
  // on a valid hex lattice. multi.sim.ts can (and, when simulating an
  // external producer's raw output, does) publish cells sampled on a plain
  // rectangular grid — see its SIMULATE_RAW_EXTERNAL_GRID flag. Rather than
  // trust the wire data's shape, every batch is run through
  // convertToHexLattice() before any polygon geometry is built, so
  // rendering is always correct regardless of what shape grid the
  // publisher actually used. When the input already IS a valid lattice
  // (our own normal hex generator), this is a fast no-op snap — every cell
  // maps to itself.
  updateBatch(payload: ProbabilityGridPayload) {
    if (!this.map.isStyleLoaded()) {
      // Style not ready yet; drop this tick rather than queueing a retry
      // loop — the simulator publishes on a steady short interval, so the
      // next batch arrives shortly and self-heals this without extra
      // bookkeeping.
      return;
    }

    this.ensureSourceAndLayer();

    const features: GeoJSON.Feature[] = [];
    let id = 0;
    for (const target of payload.targets ?? []) {
      const rawCells = target.cells ?? [];
      if (rawCells.length === 0) continue;

      // Origin for the lattice: prefer the producer's own stated origin
      // (target.origin — typically the target's true position, the same
      // reference point the producer's own scan was centered on). This
      // matters because the lattice the client snaps onto must exactly
      // match the lattice the raw scan was implicitly aligned to, or a
      // small sub-cell misalignment causes cells right at the weight
      // cutoff boundary to be missed (measured: ~2% of cells) — falling
      // back to the centroid of this batch's own cells is a reasonable
      // approximation when a producer doesn't supply origin, but is not
      // exactly stable tick-to-tick the way a fixed origin is.
      const origin = target.origin ?? {
        lat: rawCells.reduce((sum, c) => sum + c.lat, 0) / rawCells.length,
        lon: rawCells.reduce((sum, c) => sum + c.lon, 0) / rawCells.length,
      };

      const hexCells = convertToHexLattice(rawCells, {
        origin,
        hexRadiusMetres: target.hex_radius_metres,
      });

      for (const cell of hexCells) {
        features.push({
          id: id++,
          type: "Feature",
          geometry: {
            type: "Polygon",
            coordinates: [
              hexagonRing(cell.lat, cell.lon, target.hex_radius_metres),
            ],
          },
          properties: {
            weight: cell.weight,
            target_id: target.target_id,
          },
        });
      }
    }

    const source = this.map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
    if (source) {
      source.setData({ type: "FeatureCollection", features });
    }
  }

  clear() {
    const source = this.map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
    if (source) {
      source.setData({ type: "FeatureCollection", features: [] });
    }
  }

  private ensureSourceAndLayer() {
    if (this.initialised) return;

    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
    }

    if (!this.map.getLayer(LAYER_ID)) {
      // Insert before the static layer stack's bottom-most layer (the
      // point-based heatmap layer, first in layers/index.ts) so this field
      // sits underneath EVERYTHING — targets, AoA lines, aircraft, etc.
      // addLayer's default (no beforeId) stacks new layers on TOP of
      // everything currently on the map, which is wrong here since this is
      // added dynamically, well after the static stack is already in
      // place. Falls back to no beforeId (top of stack) only if that layer
      // isn't present for some reason, so this never silently no-ops.
      const beforeId = this.map.getLayer(LAYERS.HEATMAP)
        ? LAYERS.HEATMAP
        : undefined;
      this.map.addLayer(
        {
          id: LAYER_ID,
          type: "fill",
          source: SOURCE_ID,
          paint: {
            "fill-color": FILL_COLOR_EXPRESSION,
            "fill-opacity": FILL_OPACITY_EXPRESSION,
          },
        },
        beforeId,
      );
    }

    this.initialised = true;
  }

  destroy() {
    this.clear();
  }
}
